import { ClientPortfolioPaymentFrequencyEnum } from "web_myplann/Infrastructure/Api/Api";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import { refineContractRequiredMinimum } from "web_myplann/Portfolio/Forms/ContractShared/contractRequiredMinimum";
import { getManualValueShape } from "web_myplann/Portfolio/PortfolioShared/getManualValueShape";
import { refineManualValue } from "web_myplann/Portfolio/PortfolioShared/refineManualValue";
import type { TranslateFunction } from "web_myplann/Shared/TranslateFunction";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";
import { optionalDateAsString } from "web_myplann/Shared/Validation/optionalDateAsString";
import { optionalPositiveFloat } from "web_myplann/Shared/Validation/optionalPositiveFloat";
import { optionalPositiveInteger } from "web_myplann/Shared/Validation/optionalPositiveInteger";
import { refineDateOrder } from "web_myplann/Shared/Validation/refineDateOrder";
import { trimmedNullable } from "web_myplann/Shared/Validation/trimmedNullable";
import { ValidationLimits } from "web_myplann/Shared/Validation/validationLimits";
import { z } from "zod";

const getPensionBaseShape = (t: TranslateFunction) => {
	const maxMessage = t(Resources.Portfolio.AddShared.Validations.maxValue);
	const positiveNumberMessages = {
		positiveMessage: t(
			Resources.Portfolio.AddShared.Validations.positiveNumber,
		),
		maxMessage,
	};

	return {
		name: trimmedNullable(
			z.string().max(
				ValidationLimits.TextLength.LongName,
				t(Resources.Portfolio.AddShared.Validations.maxLength, {
					max: ValidationLimits.TextLength.LongName,
				}),
			),
		),
		contractNumber: trimmedNullable(
			z.string().max(
				ValidationLimits.TextLength.Name,
				t(Resources.Portfolio.AddShared.Validations.maxLength, {
					max: ValidationLimits.TextLength.Name,
				}),
			),
		),
		producer: trimmedNullable(
			z.string().max(
				ValidationLimits.TextLength.LongName,
				t(Resources.Portfolio.AddShared.Validations.maxLength, {
					max: ValidationLimits.TextLength.LongName,
				}),
			),
		),
		product: trimmedNullable(
			z.string().max(
				ValidationLimits.TextLength.LongName,
				t(Resources.Portfolio.AddShared.Validations.maxLength, {
					max: ValidationLimits.TextLength.LongName,
				}),
			),
		),
		signedAt: optionalDateAsString(DateFormat.Iso),
		startAt: optionalDateAsString(DateFormat.Iso),
		endAt: optionalDateAsString(DateFormat.Iso),
		payment: optionalPositiveFloat(
			ValidationLimits.Amount.Max,
			positiveNumberMessages,
		),
		paymentFrequency: z.nativeEnum(ClientPortfolioPaymentFrequencyEnum),
		employerContribution: optionalPositiveFloat(
			ValidationLimits.Amount.Max,
			positiveNumberMessages,
		),
		ageLimit: optionalPositiveInteger(
			ValidationLimits.Age.Max,
			positiveNumberMessages,
		),
		appreciation: optionalPositiveFloat(
			ValidationLimits.Percent.Max,
			positiveNumberMessages,
		),
	};
};

const getDateOrderRefinement = (t: TranslateFunction) =>
	refineDateOrder({
		endBeforeSigned: t(
			Resources.Portfolio.AddShared.Validations.endBeforeSigned,
		),
	});

export const getPensionCreateFormSchema = (t: TranslateFunction) =>
	z
		.object({
			...getPensionBaseShape(t),
			...getManualValueShape(t),
		})
		.superRefine(refineManualValue(t))
		.superRefine(getDateOrderRefinement(t))
		.superRefine(refineContractRequiredMinimum);

export const getPensionEditFormSchema = (t: TranslateFunction) =>
	z
		.object(getPensionBaseShape(t))
		.superRefine(getDateOrderRefinement(t))
		.superRefine(refineContractRequiredMinimum);

export type PensionCreateFormInput = z.input<
	ReturnType<typeof getPensionCreateFormSchema>
>;
export type PensionCreateFormOutput = z.output<
	ReturnType<typeof getPensionCreateFormSchema>
>;

export type PensionEditFormInput = z.input<
	ReturnType<typeof getPensionEditFormSchema>
>;
export type PensionEditFormOutput = z.output<
	ReturnType<typeof getPensionEditFormSchema>
>;
